import { __assign } from "tslib";
import { evaluateConditionResult } from './condition';
import { calcOrderItemGrandTotal } from './orderItemLogic';
import _ from 'lodash';
var OPERATORS = {
    value: function (params) {
        var operator = params.operator;
        return operator.value;
    },
    min: function (params) {
        var operator = params.operator;
        return _.min(_.map(operator.operators, function (_operator) {
            return calculateOperator(_.extend(params, { operator: _operator }));
        }));
    },
    max: function (params) {
        var operator = params.operator;
        return _.max(_.map(operator.operators, function (_operator) {
            return calculateOperator(_.extend(params, { operator: _operator }));
        }));
    },
    multiply: function (params) {
        var operator = params.operator;
        var numerators = 1;
        var denominators = 1;
        _.each(operator.numerators, function (_operator) {
            numerators *= calculateOperator(_.extend(params, { operator: _operator }));
        });
        _.each(operator.denominators, function (_operator) {
            denominators *= calculateOperator(_.extend(params, { operator: _operator }));
        });
        var result = numerators / denominators;
        return roundNumber(result, params.roundingStrategy);
    },
    count_items: function (params) {
        var operator = params.operator;
        var orderItems = params.orderItems;
        var orderCharges = params.orderCharges;
        var total = 0;
        _.each(orderItems, function (orderItem) {
            if (isApplicableItem(orderItem.itemId, operator.items)) {
                total += orderItem.count || 1;
            }
        });
        _.each(orderCharges, function (orderCharge) {
            if (isApplicableItem(orderCharge.chargeId, operator.charges)) {
                total++;
            }
        });
        return total;
    },
    sum: function (params) {
        var operator = params.operator;
        var result = 0;
        _.each(operator.operators, function (_operator) {
            result += calculateOperator(_.extend(params, { operator: _operator }));
        });
        return result;
    },
    sum_prices: function (params) {
        var operator = params.operator;
        var orderItems = params.orderItems;
        var orderCharges = params.orderCharges;
        // Get all applicable items' price in a sorted list
        var applicableItems = _.flatten(_.compact(_.map(orderItems, function (orderItem) {
            if (!isApplicableItem(orderItem.itemId, operator.items)) {
                return null;
            }
            return _.map(_.range(orderItem.count || 1), function () {
                return calcOrderItemGrandTotal(orderItem) / (orderItem.count || 1);
            });
        })));
        // Get all applicable items' price in a sorted list
        var applicableCharges = _.flatten(_.compact(_.map(orderCharges, function (orderCharge) {
            if (!isApplicableItem(orderCharge.chargeId, operator.charges)) {
                return null;
            }
            return orderCharge.amount;
        })));
        var applicable = _(applicableItems)
            .concat(applicableCharges)
            .value()
            .sort(function (x, y) {
            return x - y;
        });
        // Re-calculate Y
        var max = Math.min(operator.maxCount || Number.MAX_VALUE, applicable.length);
        // Ensure there's the right amount of items
        if (max < 0) {
            return 0;
        }
        return _.reduce(applicable.splice(0, max), function (memo, num) {
            return memo + num;
        }, 0);
    },
};
function roundNumber(n, roundingStrategy) {
    switch (roundingStrategy) {
        case 'HALF_EVEN':
            return halfEvenRounding(n);
        case 'HALF_UP':
            return halfUpRounding(n);
        case 'ALWAYS_DOWN':
            return alwaysDownRounding(n);
        case 'ALWAYS_UP':
            return alwaysUpRounding(n);
        case 'HALF_AWAY_FROM_ZERO':
        default:
            return halfAwayFromZeroRounding(n);
    }
}
function halfAwayFromZeroRounding(num) {
    return Math.sign(num) * Math.floor(Math.abs(num) + 0.5);
}
function halfEvenRounding(num) {
    var d = 0;
    var m = Math.pow(10, d);
    var n = +(d ? num * m : num).toFixed(8); // Avoid rounding errors
    var i = Math.floor(n), f = n - i;
    var e = 1e-8; // Allow for rounding errors in f
    var r = f > 0.5 - e && f < 0.5 + e ? (i % 2 === 0 ? i : i + 1) : Math.round(n);
    return d ? r / m : r;
}
function halfUpRounding(num) {
    return Math.floor(num + 0.5);
}
function alwaysDownRounding(num) {
    return Math.floor(num);
}
function alwaysUpRounding(num) {
    return Math.ceil(num);
}
function calculateOperator(params) {
    var operator = params.operator;
    var func = OPERATORS[operator.type] ||
        function () {
            return 0;
        };
    var ret = func(params);
    return ret;
}
function isApplicableItem(itemId, idsFilter) {
    if (!idsFilter) {
        return false;
    }
    if ((idsFilter.type || 'include') === 'include') {
        return _.includes(idsFilter.ids, itemId);
    }
    else {
        return !_.includes(idsFilter.ids, itemId);
    }
}
export function isApplicable(params) {
    var charge = params.charge;
    var deliveryTime = params.dispatchTime;
    var deliveryType = params.dispatchType || '';
    var totalOrderPrice = params.totalOrderPrice;
    var platform = params.platform;
    var couponHashCode = params.couponHashCode;
    if (charge.state === 'closed') {
        return false;
    }
    return evaluateConditionResult(charge.condition, {
        dispatchTime: deliveryTime,
        dispatchType: deliveryType,
        totalOrderPrice: totalOrderPrice,
        platform: platform,
        couponHashCode: couponHashCode,
    });
}
export function isDisplayable(params) {
    var charge = params.charge;
    var deliveryTime = params.dispatchTime;
    var deliveryType = params.dispatchType || '';
    var platform = params.platform;
    var couponHashCode = params.couponHashCode;
    var totalOrderPrice = params.totalOrderPrice;
    if (charge.state === 'closed') {
        return false;
    }
    return evaluateConditionResult(charge.condition, {
        dispatchTime: deliveryTime,
        dispatchType: deliveryType,
        totalOrderPrice: totalOrderPrice,
        platform: platform,
        couponHashCode: couponHashCode,
    });
}
export function isRestrictedByOrderItems(params) {
    var amount = calculateAmount(params);
    var extractValues = function (o, path) {
        if (!o || typeof o !== 'object') {
            return [];
        }
        var values = _.get(o, path) || [];
        var nextValues = Object.keys(o).map(function (key) {
            return extractValues(o[key], path);
        });
        return values.concat(_.flatten(nextValues));
    };
    if (!amount) {
        var itemIds = extractValues(params.charge.operator, 'items.ids');
        var orderItems = itemIds.map(function (id) { return ({ itemId: id, price: 10000 }); });
        var otherAmount = calculateAmount(__assign(__assign({}, params), { orderItems: orderItems }));
        return !!otherAmount;
    }
    return false;
}
export function calculateAmount(params) {
    var charge = params.charge;
    var tip = params.tip || 0;
    var orderItems = params.orderItems || [];
    var orderCharges = params.orderCharges || [];
    var roundingStrategy = params.roundingStrategy;
    if (!isApplicable(params)) {
        return 0;
    }
    if (charge.type === 'tip') {
        return tip;
    }
    return calculateOperator({
        charge: params.charge,
        operator: charge.operator,
        orderItems: orderItems,
        orderCharges: orderCharges,
        roundingStrategy: roundingStrategy,
    });
}
//# sourceMappingURL=chargesV2.js.map